import { cacheExchange, Client, fetchExchange, mapExchange, subscriptionExchange } from "urql"
import { createClient as createWSClient, type Client as WSClient } from "graphql-ws"
import { setWsStatus } from "@/hooks/use-ws-status"

const PUBLIC_PATHS = ["/login", "/signup", "/auth/verify"]

const sessionExpiryExchange = mapExchange({
  onResult(result) {
    const status = result.error?.response?.status
    if (status === 401 && !PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p))) {
      window.location.assign("/login?error=expired")
    }
    return result
  },
})

const STORAGE_KEY = "hivehook_api_key"

function getApiKey(): string {
  return localStorage.getItem(STORAGE_KEY) ?? ""
}

function getWSUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:"
  return `${proto}//${window.location.host}/graphql`
}

function buildWSClient(apiKey: string): WSClient {
  return createWSClient({
    url: getWSUrl,
    lazy: true,
    retryAttempts: Infinity,
    connectionParams: () => {
      if (apiKey) {
        return { authorization: `Bearer ${apiKey}` }
      }
      return {}
    },
    on: {
      connecting: () => setWsStatus("connecting"),
      connected: () => setWsStatus("connected"),
      closed: () => setWsStatus("disconnected"),
    },
  })
}

let currentKey = getApiKey()
let wsClient = buildWSClient(currentKey)

export function createClient(apiKey?: string): Client {
  const key = apiKey ?? getApiKey()
  return new Client({
    url: "/graphql",
    requestPolicy: "cache-and-network",
    exchanges: [
      cacheExchange,
      sessionExpiryExchange,
      subscriptionExchange({
        forwardSubscription(request) {
          const input = { ...request, query: request.query ?? "" }
          return {
            subscribe(sink) {
              const unsubscribe = wsClient.subscribe(input, sink as Parameters<typeof wsClient.subscribe>[1])
              return { unsubscribe }
            },
          }
        },
      }),
      fetchExchange,
    ],
    fetchOptions: () => {
      // Send the session cookie on every request so the SaaS binary's
      // session middleware can authenticate browser users without an
      // API key. Self-host users still send the Bearer token alongside.
      const opts: RequestInit = { credentials: "include" }
      if (key) {
        opts.headers = { Authorization: `Bearer ${key}` }
      }
      return opts
    },
  })
}

export function resetClient(apiKey?: string): Client {
  const key = apiKey ?? getApiKey()
  if (key === currentKey) {
    return createClient(key)
  }
  currentKey = key
  wsClient.dispose()
  wsClient = buildWSClient(key)
  return createClient(key)
}
